import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, Pin, Sparkles, Eye, EyeOff } from "lucide-react";
import { api } from "@/api/client";
import { useManageBusiness } from "@/pages/manage/ManageShell";
import { AuthImage } from "@/components/AuthImage";
import { money } from "@/lib/pricing";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const empty = (businessId) => ({
  business_id: businessId,
  title: "",
  description: "",
  image_url: "",
  product_id: "",
  product_ids: [],
  is_visible: true,
  sale_price: "",
  original_price: "",
  badge_text: "SPECIAL",
  valid_from: "",
  valid_until: "",
  pinned: false,
});

export default function Specials() {
  const { businessId } = useManageBusiness();
  const [specials, setSpecials] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const reload = () => {
    setLoading(true);
    Promise.all([api.listSpecials(businessId), api.listProducts(businessId)])
      .then(([s, p]) => { setSpecials(s); setProducts(p); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(reload, [businessId]);

  const remove = async (s) => {
    if (!window.confirm(`Delete special "${s.title}"?`)) return;
    try {
      await api.deleteSpecial(s.id);
      toast.success("Deleted");
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const togglePin = async (s) => {
    try {
      await api.updateSpecial(s.id, { pinned: !s.pinned });
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggleVisible = async (s) => {
    try {
      await api.updateSpecial(s.id, { is_visible: s.is_visible === false });
      toast.success(s.is_visible === false ? "Now visible to customers" : "Hidden from customers");
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl" style={{ color: "var(--ink)" }}>Specials</h1>
        <button onClick={() => setEditing(empty(businessId))} data-testid="add-special-btn" className="btn-brand px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5">
          <Plus size={16} /> New special
        </button>
      </div>

      {loading ? (
        <div className="mt-6 grid gap-4 sm:grid-cols-2">{Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-40 rounded-2xl animate-pulse" style={{ background: "var(--surface-2)" }} />)}</div>
      ) : specials.length === 0 ? (
        <div className="mt-10 text-center py-14 rounded-2xl" style={{ background: "var(--surface)", border: "1px dashed var(--border)" }}>
          <Sparkles className="mx-auto mb-2" style={{ color: "var(--accent)" }} />
          <p className="font-display text-xl" style={{ color: "var(--ink)" }}>No specials yet</p>
          <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>Promote a product or run a standalone deal.</p>
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {specials.map((s) => (
            <div key={s.id} data-testid={`admin-special-${s.id}`} className="rounded-2xl overflow-hidden" style={{ background: "var(--surface)", border: "1px solid var(--border)", opacity: s.is_ended ? 0.7 : 1 }}>
              <div className="relative aspect-[16/9]" style={{ background: "var(--surface-2)" }}>
                {s.image_url ? <AuthImage src={s.image_url} alt={s.title} className="h-full w-full object-cover" /> : <div className="h-full flex items-center justify-center"><Sparkles size={32} style={{ color: "var(--accent)" }} /></div>}
                <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase" style={{ background: s.is_ended ? "#9ca3af" : "var(--accent)", color: "#1a1400" }}>{s.is_ended ? "Ended" : s.badge_text}</span>
                {s.is_visible === false && <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase" style={{ background: "#4b5563", color: "#fff" }}>Hidden</span>}
                {s.pinned && <span className="absolute top-2 right-2 h-6 w-6 rounded-full flex items-center justify-center" style={{ background: "var(--brand)", color: "var(--brand-fg)" }}><Pin size={12} /></span>}
              </div>
              <div className="p-4">
                <h3 className="font-display text-lg" style={{ color: "var(--ink)" }}>{s.title}</h3>
                <p className="text-sm line-clamp-2" style={{ color: "var(--muted)" }}>{s.description}</p>
                {Array.isArray(s.product_ids) && s.product_ids.length > 0 && (
                  <p className="mt-1 text-xs font-semibold" style={{ color: "var(--brand)" }}>{s.product_ids.length} item{s.product_ids.length > 1 ? "s" : ""} in this group</p>
                )}
                {s.sale_price != null && (
                  <div className="mt-1.5 flex items-baseline gap-2">
                    <span className="font-semibold" style={{ color: "var(--brand)" }}>{money(s.sale_price)}</span>
                    {s.original_price != null && <span className="text-sm line-through" style={{ color: "var(--muted)" }}>{money(s.original_price)}</span>}
                  </div>
                )}
                <div className="mt-3 flex gap-2">
                  <button onClick={() => toggleVisible(s)} data-testid={`toggle-visible-special-${s.id}`} className="flex-1 rounded-lg py-1.5 text-xs font-semibold inline-flex items-center justify-center gap-1" style={{ border: "1px solid var(--border)", color: s.is_visible === false ? "var(--muted)" : "var(--brand)" }}>
                    {s.is_visible === false ? <><EyeOff size={13} /> Hidden</> : <><Eye size={13} /> Visible</>}
                  </button>
                  <button onClick={() => togglePin(s)} data-testid={`pin-special-${s.id}`} className="rounded-lg py-1.5 px-2.5 text-xs font-semibold inline-flex items-center justify-center gap-1" style={{ border: "1px solid var(--border)", color: s.pinned ? "var(--brand)" : "var(--muted)" }}><Pin size={13} /></button>
                  <button onClick={() => setEditing({ ...s, sale_price: s.sale_price ?? "", original_price: s.original_price ?? "", product_id: s.product_id ?? "", product_ids: s.product_ids ?? [], is_visible: s.is_visible !== false, valid_from: s.valid_from ?? "", valid_until: s.valid_until ?? "" })} data-testid={`edit-special-${s.id}`} className="rounded-lg py-1.5 px-2.5 text-xs font-semibold inline-flex items-center justify-center gap-1" style={{ border: "1px solid var(--border)", color: "var(--ink)" }}><Pencil size={13} /></button>
                  <button onClick={() => remove(s)} data-testid={`delete-special-${s.id}`} className="rounded-lg py-1.5 px-2.5 text-red-600" style={{ border: "1px solid #fca5a5" }}><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <SpecialForm initial={editing} businessId={businessId} products={products} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}

function SpecialForm({ initial, businessId, products, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isNew = !initial.id;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.uploadImage(file);
      set("image_url", res.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    const toIso = (v) => (v ? new Date(v).toISOString() : null);
    const body = {
      business_id: businessId,
      title: f.title.trim(),
      description: f.description || "",
      image_url: f.image_url || null,
      product_id: f.product_id || null,
      product_ids: f.product_ids || [],
      is_visible: f.is_visible !== false,
      sale_price: f.sale_price === "" ? null : parseFloat(f.sale_price),
      original_price: f.original_price === "" ? null : parseFloat(f.original_price),
      badge_text: f.badge_text || "SPECIAL",
      valid_from: toIso(f.valid_from),
      valid_until: toIso(f.valid_until),
      pinned: !!f.pinned,
    };
    setSaving(true);
    try {
      if (isNew) await api.createSpecial(body);
      else await api.updateSpecial(initial.id, body);
      toast.success(isNew ? "Special created" : "Special updated");
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { background: "var(--surface)", border: "1px solid var(--border)" };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">{isNew ? "New special" : "Edit special"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-semibold" style={{ color: "var(--ink)" }}>Image (optional)</label>
            <div className="mt-1.5 flex items-center gap-3">
              <div className="h-20 w-28 rounded-xl overflow-hidden shrink-0" style={{ background: "var(--surface-2)" }}>{f.image_url && <AuthImage src={f.image_url} alt="" className="h-full w-full object-cover" />}</div>
              <label className="cursor-pointer rounded-xl px-4 py-2 text-sm font-semibold inline-flex items-center gap-2" style={inputStyle}>
                <Upload size={15} /> {uploading ? "Uploading…" : "Upload"}
                <input type="file" accept="image/*" onChange={upload} data-testid="special-image-input" className="hidden" />
              </label>
            </div>
          </div>

          <Field label="Title"><input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="special-title" className="w-full rounded-xl px-3 py-2 outline-none" style={inputStyle} /></Field>
          <Field label="Description"><textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={2} data-testid="special-description" className="w-full rounded-xl px-3 py-2 outline-none resize-none" style={inputStyle} /></Field>

          <Field label="Link to product (optional)">
            <select value={f.product_id} onChange={(e) => { const pid = e.target.value; set("product_id", pid); const p = products.find((x) => x.id === pid); if (p && f.original_price === "") set("original_price", p.price); }} data-testid="special-product" className="w-full rounded-xl px-3 py-2 outline-none" style={inputStyle}>
              <option value="">Standalone deal</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({money(p.price)})</option>)}
            </select>
          </Field>

          <Field label="Group items (shown together on the shop)">
            <div className="rounded-xl p-2 max-h-48 overflow-y-auto space-y-1" style={inputStyle} data-testid="special-group-items">
              {products.length === 0 ? (
                <p className="text-sm px-1 py-2" style={{ color: "var(--muted)" }}>No products yet.</p>
              ) : products.map((p) => {
                const checked = (f.product_ids || []).includes(p.id);
                return (
                  <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer text-sm" style={{ color: "var(--ink)", background: checked ? "var(--surface-2)" : "transparent" }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      data-testid={`special-group-item-${p.id}`}
                      onChange={(e) => {
                        const cur = new Set(f.product_ids || []);
                        if (e.target.checked) cur.add(p.id); else cur.delete(p.id);
                        set("product_ids", Array.from(cur));
                      }}
                    />
                    <span className="flex-1 truncate">{p.name}</span>
                    <span className="text-xs" style={{ color: "var(--muted)" }}>{p.category}</span>
                  </label>
                );
              })}
            </div>
            {(f.product_ids || []).length > 0 && (
              <p className="mt-1 text-xs" style={{ color: "var(--muted)" }}>{f.product_ids.length} selected — shown as a group section on the shop.</p>
            )}
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Sale price ($)"><input type="number" value={f.sale_price} onChange={(e) => set("sale_price", e.target.value)} data-testid="special-sale-price" className="w-full rounded-xl px-3 py-2 outline-none" style={inputStyle} /></Field>
            <Field label="Original price ($)"><input type="number" value={f.original_price} onChange={(e) => set("original_price", e.target.value)} className="w-full rounded-xl px-3 py-2 outline-none" style={inputStyle} /></Field>
          </div>

          <Field label="Badge text"><input value={f.badge_text} onChange={(e) => set("badge_text", e.target.value)} data-testid="special-badge" className="w-full rounded-xl px-3 py-2 outline-none" style={inputStyle} /></Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Valid from"><input type="date" value={f.valid_from ? f.valid_from.slice(0, 10) : ""} onChange={(e) => set("valid_from", e.target.value)} className="w-full rounded-xl px-3 py-2 outline-none" style={inputStyle} /></Field>
            <Field label="Valid until"><input type="date" value={f.valid_until ? f.valid_until.slice(0, 10) : ""} onChange={(e) => set("valid_until", e.target.value)} className="w-full rounded-xl px-3 py-2 outline-none" style={inputStyle} /></Field>
          </div>

          <label className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--ink)" }}>
            <input type="checkbox" checked={f.pinned} onChange={(e) => set("pinned", e.target.checked)} data-testid="special-pinned" /> Pin as shop banner
          </label>

          <label className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--ink)" }}>
            <input type="checkbox" checked={f.is_visible !== false} onChange={(e) => set("is_visible", e.target.checked)} data-testid="special-visible" /> Visible to customers
          </label>

          <button onClick={save} disabled={saving || uploading} data-testid="save-special-btn" className="btn-brand w-full rounded-xl py-3 font-semibold disabled:opacity-60">{saving ? "Saving…" : isNew ? "Create special" : "Save changes"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm font-semibold block mb-1.5" style={{ color: "var(--ink)" }}>{label}</label>
      {children}
    </div>
  );
}
